// src/components/CommonComponents/Pagination.js
import React from "react";

const findScrollableParent = (element) => {
    let parent = element?.parentElement;
    while (parent && parent !== document.body) {
        const styles = window.getComputedStyle(parent);
        const overflowY = styles.overflowY;
        if ((overflowY === "auto" || overflowY === "scroll") && parent.scrollHeight > parent.clientHeight) {
            return parent;
        }
        parent = parent.parentElement;
    }
    return window;
};

const Pagination = ({ currentPage, totalPages, onPageChange, totalCount, scrollTargetSelector }) => {
    const scrollAfterPageChange = (sourceElement) => {
        window.requestAnimationFrame(() => {
            const explicitTarget = scrollTargetSelector ? document.querySelector(scrollTargetSelector) : null;
            const nearestTable = sourceElement.closest("[data-pagination-scroll-root]")?.querySelector("[data-pagination-scroll-target]");
            const target = explicitTarget || nearestTable;

            if (target) {
                target.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }

            const scrollParent = findScrollableParent(sourceElement);
            if (scrollParent === window) {
                window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
                scrollParent.scrollTo({ top: 0, behavior: "smooth" });
            }
        });
    };

    const handlePageChange = (page, event) => {
        if (page === currentPage) return;
        onPageChange(page);
        scrollAfterPageChange(event.currentTarget);
    };

    const prevPage = (event) => currentPage > 1 && handlePageChange(currentPage - 1, event);
    const nextPage = (event) => currentPage < totalPages && handlePageChange(currentPage + 1, event);

    // Define window size (max 10 pages shown)
    const maxPagesToShow = 10;
    const startPage = Math.floor((currentPage - 1) / maxPagesToShow) * maxPagesToShow + 1;
    const endPage = Math.min(startPage + maxPagesToShow - 1, totalPages);

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    return (
        <div className="w-full flex items-center">
            <div className="flex-1" />
            <div className="flex items-center gap-1">
            {/* Prev button */}
            <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`px-2 py-1 rounded-md text-xs font-medium focus:outline-none ${currentPage === 1
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "text-white"
                    }`}
                style={currentPage === 1 ? {} : {
                    background: 'var(--color-primary)',
                }}
                onMouseOver={currentPage === 1 ? undefined : (e => e.currentTarget.style.background = 'var(--color-hover)')}
                onMouseOut={currentPage === 1 ? undefined : (e => e.currentTarget.style.background = 'var(--color-primary)')}
            >
                Prev
            </button>

            {/* Page numbers (max 10) */}
            {pages.map((page) => (
                <button
                    key={page}
                onClick={(event) => handlePageChange(page, event)}
                    className={`px-2 py-1 rounded-md text-xs font-semibold border focus:outline-none ${currentPage === page
                        ? "text-white"
                        : "bg-white hover:bg-gray-100"
                        }`}
                    style={currentPage === page
                        ? { background: 'var(--color-primary)', borderColor: 'var(--color-primary)' }
                        : { color: 'var(--color-primary)', borderColor: 'var(--color-primary)' }
                    }
                >
                    {page}
                </button>
            ))}

            {/* Next button */}
            <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`px-2 py-1 rounded-md text-xs font-medium focus:outline-none ${currentPage === totalPages
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "text-white"
                    }`}
                style={currentPage === totalPages ? {} : {
                    background: 'var(--color-primary)',
                }}
                onMouseOver={currentPage === totalPages ? undefined : (e => e.currentTarget.style.background = 'var(--color-hover)')}
                onMouseOut={currentPage === totalPages ? undefined : (e => e.currentTarget.style.background = 'var(--color-primary)')}
            >
                Next
            </button>
            </div>
            <div className="flex-1 flex justify-end">
                {typeof totalCount === "number" && (
                    <span className="text-xs text-gray-500">Total: {totalCount}</span>
                )}
            </div>
        </div>
    );
};

export default Pagination;
